import React, { useEffect, useState, memo } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup
} from 'react-simple-maps';
import { getCountryStats, getTotalCountryStats } from '../services/statsApi';
import colors from '../theme/colors';

// TopoJSON world map URL
const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${colors.background.overlay};
  z-index: 10999;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModalContent = styled(motion.div)`
  background: linear-gradient(145deg, #1a1a2e 0%, #16213e 100%);
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.5);
  z-index: 11000;
  width: 90vw;
  max-width: 1000px;
  max-height: 90vh;
  overflow-y: auto;
  color: white;
  border: 1px solid rgba(255,255,255,0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.4rem;
  color: ${colors.accent.primary};
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 1.8rem;
  cursor: pointer;
  padding: 5px 10px;
  opacity: 0.7;
  transition: opacity 0.2s;
  line-height: 1;

  &:hover {
    opacity: 1;
  }
`;

const StatsContainer = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const StatBadge = styled.div`
  background: rgba(255, 255, 255, 0.08);
  padding: 12px 18px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid rgba(255,255,255,0.1);

  span:first-child {
    font-size: 1.3rem;
  }

  span:last-child {
    font-weight: 600;
    font-size: 1rem;
  }
`;

const MapContainer = styled.div`
  width: 100%;
  margin-bottom: 20px;
  background: linear-gradient(180deg, #0f0f23 0%, #1a1a35 100%);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,0.1);

  svg {
    display: block;
  }
`;

const CountryList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;
  max-height: 200px;
  overflow-y: auto;
  padding: 15px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.05);
`;

const CountryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  .country-name {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.95rem;
  }

  .count {
    font-weight: 700;
    color: ${colors.accent.primary};
    font-size: 1rem;
  }
`;

const Tooltip = styled.div`
  position: fixed;
  background: rgba(0, 0, 0, 0.95);
  color: white;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.9rem;
  pointer-events: none;
  z-index: 12000;
  display: ${props => props.$visible ? 'block' : 'none'};
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  border: 1px solid rgba(255,255,255,0.1);

  .country {
    font-weight: 600;
    margin-bottom: 4px;
  }

  .count {
    color: ${colors.accent.primary};
  }
`;

// Country name mapping (ISO 3166-1 numeric to name)
const COUNTRY_NAMES = {
  '004': 'Afghanistan', '008': 'Albania', '012': 'Algeria', '020': 'Andorra',
  '024': 'Angola', '032': 'Argentina', '036': 'Australia', '040': 'Austria',
  '050': 'Bangladesh', '056': 'Belgium', '076': 'Brazil', '100': 'Bulgaria',
  '124': 'Canada', '152': 'Chile', '156': 'China', '170': 'Colombia',
  '191': 'Croatia', '196': 'Cyprus', '203': 'Czech Republic', '208': 'Denmark',
  '218': 'Ecuador', '818': 'Egypt', '233': 'Estonia', '246': 'Finland',
  '250': 'France', '276': 'Germany', '300': 'Greece', '344': 'Hong Kong',
  '348': 'Hungary', '352': 'Iceland', '356': 'India', '360': 'Indonesia',
  '364': 'Iran', '368': 'Iraq', '372': 'Ireland', '376': 'Israel',
  '380': 'Italy', '392': 'Japan', '400': 'Jordan', '404': 'Kenya',
  '410': 'South Korea', '414': 'Kuwait', '428': 'Latvia', '422': 'Lebanon',
  '440': 'Lithuania', '442': 'Luxembourg', '458': 'Malaysia', '484': 'Mexico',
  '504': 'Morocco', '528': 'Netherlands', '554': 'New Zealand', '566': 'Nigeria',
  '578': 'Norway', '586': 'Pakistan', '604': 'Peru', '608': 'Philippines',
  '616': 'Poland', '620': 'Portugal', '634': 'Qatar', '642': 'Romania',
  '643': 'Russia', '682': 'Saudi Arabia', '688': 'Serbia', '702': 'Singapore',
  '703': 'Slovakia', '705': 'Slovenia', '710': 'South Africa', '724': 'Spain',
  '752': 'Sweden', '756': 'Switzerland', '158': 'Taiwan', '764': 'Thailand',
  '792': 'Turkey', '804': 'Ukraine', '784': 'United Arab Emirates',
  '826': 'United Kingdom', '840': 'United States', '858': 'Uruguay',
  '862': 'Venezuela', '704': 'Vietnam'
};

// ISO alpha-2 to numeric mapping (for our API which uses alpha-2)
const ALPHA2_TO_NUMERIC = {
  'AF': '004', 'AL': '008', 'DZ': '012', 'AD': '020', 'AO': '024',
  'AR': '032', 'AU': '036', 'AT': '040', 'BD': '050', 'BE': '056',
  'BR': '076', 'BG': '100', 'CA': '124', 'CL': '152', 'CN': '156',
  'CO': '170', 'HR': '191', 'CY': '196', 'CZ': '203', 'DK': '208',
  'EC': '218', 'EG': '818', 'EE': '233', 'FI': '246', 'FR': '250',
  'DE': '276', 'GR': '300', 'HK': '344', 'HU': '348', 'IS': '352',
  'IN': '356', 'ID': '360', 'IR': '364', 'IQ': '368', 'IE': '372',
  'IL': '376', 'IT': '380', 'JP': '392', 'JO': '400', 'KE': '404',
  'KR': '410', 'KW': '414', 'LV': '428', 'LB': '422', 'LT': '440',
  'LU': '442', 'MY': '458', 'MX': '484', 'MA': '504', 'NL': '528',
  'NZ': '554', 'NG': '566', 'NO': '578', 'PK': '586', 'PE': '604',
  'PH': '608', 'PL': '616', 'PT': '620', 'QA': '634', 'RO': '642',
  'RU': '643', 'SA': '682', 'RS': '688', 'SG': '702', 'SK': '703',
  'SI': '705', 'ZA': '710', 'ES': '724', 'SE': '752', 'CH': '756',
  'TW': '158', 'TH': '764', 'TR': '792', 'UA': '804', 'AE': '784',
  'GB': '826', 'US': '840', 'UY': '858', 'VE': '862', 'VN': '704',
  'XX': 'XX'
};

// Get color based on count intensity
const getCountryColor = (countryId, countryDataMap, maxCount) => {
  const count = countryDataMap.get(countryId) || 0;
  if (count === 0) return '#2a2a4a'; // Default gray

  const intensity = Math.min(count / maxCount, 1);
  // Gradient from dark blue (#3b5998) to gold (#fbbf24)
  const r = Math.round(59 + (251 - 59) * intensity);
  const g = Math.round(89 + (191 - 89) * intensity);
  const b = Math.round(152 + (36 - 152) * intensity);
  return `rgb(${r}, ${g}, ${b})`;
};

// Memoized Geography component for performance
const MemoizedGeography = memo(({ geo, fill, onMouseEnter, onMouseLeave }) => (
  <Geography
    geography={geo}
    fill={fill}
    stroke="#1a1a2e"
    strokeWidth={0.5}
    style={{
      default: { outline: 'none' },
      hover: { outline: 'none', fill: '#fbbf24', cursor: 'pointer' },
      pressed: { outline: 'none' }
    }}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
  />
));

const WorldMapModal = ({ isOpen, onClose, nodeId, nodeTitle, type = 'anonymous' }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [countryData, setCountryData] = useState([]);
  const [tooltip, setTooltip] = useState({ visible: false, content: '', x: 0, y: 0 });

  // Create a map of country numeric ID -> count for fast lookup
  const countryDataMap = new Map();
  countryData.forEach(({ countryCode, count }) => {
    const numericId = ALPHA2_TO_NUMERIC[countryCode];
    if (numericId) {
      countryDataMap.set(numericId, count);
    }
  });

  const maxCount = Math.max(...countryData.map(c => c.count), 1);
  const totalFlips = countryData.reduce((sum, c) => sum + c.count, 0);
  const uniqueCountries = countryData.filter(c => c.countryCode !== 'XX').length;

  useEffect(() => {
    if (isOpen && nodeId) {
      setLoading(true);
      // If nodeId is 'total', fetch total stats across all nodes
      const fetchStats = nodeId === 'total'
        ? getTotalCountryStats(type)
        : getCountryStats(nodeId, type);

      fetchStats.then(data => {
        setCountryData(data.countries || []);
        setLoading(false);
      });
    }
  }, [isOpen, nodeId, type]);

  const handleMouseEnter = (geo, event) => {
    const countryId = geo.id;
    const countryName = geo.properties?.name || COUNTRY_NAMES[countryId] || 'Unknown';
    const count = countryDataMap.get(countryId) || 0;

    setTooltip({
      visible: true,
      content: { name: countryName, count },
      x: event.clientX + 15,
      y: event.clientY + 15
    });
  };

  const handleMouseLeave = () => {
    setTooltip({ ...tooltip, visible: false });
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  const icon = type === 'anonymous' ? '👤' : '🔐';
  const typeLabel = type === 'anonymous'
    ? (t('admin.anonymousFlips') || 'Anonymous Flips')
    : (t('admin.authenticatedFlips') || 'Authenticated Flips');

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          >
            <ModalContent
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <ModalTitle>
                  {icon} {typeLabel}
                  {nodeTitle && (
                    <span style={{ opacity: 0.6, fontSize: '0.85em', fontWeight: 400 }}>
                      — {nodeTitle}
                    </span>
                  )}
                </ModalTitle>
                <CloseButton onClick={onClose}>&times;</CloseButton>
              </ModalHeader>

              <StatsContainer>
                <StatBadge>
                  <span>🌍</span>
                  <span>{t('admin.totalFlips') || 'Total'}: {totalFlips}</span>
                </StatBadge>
                <StatBadge>
                  <span>🗺️</span>
                  <span>{t('admin.countries') || 'Countries'}: {uniqueCountries}</span>
                </StatBadge>
              </StatsContainer>

              {loading ? (
                <div style={{ textAlign: 'center', padding: '60px', opacity: 0.7 }}>
                  {t('common.loading') || 'Loading...'}
                </div>
              ) : (
                <>
                  <MapContainer>
                    <ComposableMap
                      projection="geoMercator"
                      projectionConfig={{
                        scale: 130,
                        center: [0, 30]
                      }}
                      style={{ width: '100%', height: 'auto' }}
                    >
                      <ZoomableGroup>
                        <Geographies geography={GEO_URL}>
                          {({ geographies }) =>
                            geographies.map((geo) => (
                              <MemoizedGeography
                                key={geo.rsmKey}
                                geo={geo}
                                fill={getCountryColor(geo.id, countryDataMap, maxCount)}
                                onMouseEnter={(e) => handleMouseEnter(geo, e)}
                                onMouseLeave={handleMouseLeave}
                              />
                            ))
                          }
                        </Geographies>
                      </ZoomableGroup>
                    </ComposableMap>
                  </MapContainer>

                  {countryData.length > 0 && (
                    <CountryList>
                      {countryData
                        .filter(c => c.countryCode !== 'XX')
                        .map(({ countryCode, count }) => {
                          const numericId = ALPHA2_TO_NUMERIC[countryCode];
                          const name = COUNTRY_NAMES[numericId] || countryCode;
                          return (
                            <CountryItem key={countryCode}>
                              <div className="country-name">{name}</div>
                              <span className="count">{count}</span>
                            </CountryItem>
                          );
                        })}
                      {countryData.find(c => c.countryCode === 'XX') && (
                        <CountryItem>
                          <div className="country-name">Unknown</div>
                          <span className="count">
                            {countryData.find(c => c.countryCode === 'XX')?.count || 0}
                          </span>
                        </CountryItem>
                      )}
                    </CountryList>
                  )}

                  {countryData.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '30px', opacity: 0.6 }}>
                      {t('admin.noFlipsYet') || 'No flips recorded yet'}
                    </div>
                  )}
                </>
              )}
            </ModalContent>
          </ModalOverlay>

          <Tooltip
            $visible={tooltip.visible}
            style={{ left: tooltip.x, top: tooltip.y }}
          >
            <div className="country">{tooltip.content?.name}</div>
            <div className="count">
              {tooltip.content?.count || 0} {tooltip.content?.count === 1 ? 'flip' : 'flips'}
            </div>
          </Tooltip>
        </>
      )}
    </AnimatePresence>
  );
};

export default WorldMapModal;
